/**
 * How a new schedule relates to the request periods: which period it can
 * match, and which overlapping periods still have requests awaiting the
 * admin's approval. Pure, so the Build page and the draft toolbar stay thin.
 */
import { addDays, compareDates, mondayOnOrBefore, type ISODate } from '../dates/iso.ts';

/** The parts of a request period these rules read. */
export interface PeriodLike {
  startDate: ISODate;
  endDate: ISODate;
  open: boolean;
  pendingCount: number;
}

/**
 * The last day of a period's last week. A period's end may fall mid-week;
 * a schedule built over it runs to that week's Sunday, as the server's
 * whole-weeks rule does.
 */
export function periodWeeksEnd(endDate: ISODate): ISODate {
  return addDays(mondayOnOrBefore(endDate), 6);
}

/**
 * The request period that starts on the schedule's first Monday, offered on
 * Build as "Match <period>". An open period wins over a closed one (closing
 * a period after its due date is normal, so closed ones count too); among
 * equals, the first in the list, which the server sorts newest first.
 */
export function periodStartingOn<P extends PeriodLike>(periods: readonly P[], start: ISODate): P | null {
  if (!start) return null;
  const monday = mondayOnOrBefore(start);
  const matches = periods.filter((p) => mondayOnOrBefore(p.startDate) === monday);
  return matches.find((p) => p.open) ?? matches[0] ?? null;
}

/**
 * The periods overlapping a schedule's dates that still have requests
 * awaiting approval. The generator uses approved availability only, so the
 * admin is told about these before generating, and again on the draft.
 */
export function periodsAwaitingApproval<P extends PeriodLike>(periods: readonly P[], start: ISODate, end: ISODate): P[] {
  if (!start || !end || compareDates(end, start) < 0) return [];
  return periods.filter((p) => p.pendingCount > 0 && compareDates(p.startDate, end) <= 0 && compareDates(p.endDate, start) >= 0);
}
